import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'environments/environment';
import { KindeAuthService } from './kinde-auth.service';

/**
 * Event payloads from socket.io server
 */
export interface StatementExtractionStatusEvent {
  extraction_status: string;
  header_extracted?: boolean;
  vendor_line_items_extracted?: boolean;
  ap_items_collected?: boolean;
  statement_id: string;
  message?: string;
}

export interface ApItemsStatusEvent {
  ap_items_status: 'COLLECTED' | 'PENDING' | 'FAILED';
  statement_id: string;
  error?: string;
  extraction_message?: string;
}

export interface ErpConnectionStatusEvent {
  status: 'CONNECTING' | 'CONNECTED' | 'EXTRACTING' | 'EXTRACTED' | 'DISCONNECTING' | 'DISCONNECTED' | 'FAILED';
  erp_type: 'QUICKBOOKS' | 'XERO' | 'VIEWPOINT' | 'IMPORTED';
  organization_id?: string;
  connection_status?: 'CONNECTING' | 'DISCONNECTING' | 'DISCONNECTED';
  file_name?: string;
  error?: string;
  error_code?: string;
  extraction_progress?: number;
}

export interface ErpExtractionProgressEvent {
  step: 'organization_imported' | 'vendors_imported' | 'ap_items_importing' | 'ap_items_imported';
  erp_type: 'QUICKBOOKS' | 'XERO' | 'VIEWPOINT' | 'IMPORTED';
  organization_id?: string;
  organization_name?: string;
  vendor_count?: number;
  batch_size?: number;
}

export interface MatchingStatusEvent {
  matching_id: string;
  status: string;
  error?: string;
}

/**
 * SocketService - Manages real-time WebSocket connections to the backend
 *
 * This service replaces setInterval polling with Socket.IO event-based updates
 * for statement extraction, AP items collection, and ERP connection status.
 *
 * Usage:
 * 1. Connect to socket (automatically done on service instantiation)
 * 2. Subscribe to specific rooms (company, statement, or ERP)
 * 3. Listen to event observables
 * 4. Unsubscribe from rooms when done
 * 5. Disconnect socket when appropriate
 */
@Injectable({
  providedIn: 'root'
})
export class SocketService implements OnDestroy {

  private socket: Socket | null = null;
  private connectionStatus$ = new BehaviorSubject<boolean>(false);

  // Event subjects for emitting data to subscribers
  private statementExtractionStatus$ = new Subject<StatementExtractionStatusEvent>();
  private apItemsStatus$ = new Subject<ApItemsStatusEvent>();
  private erpConnectionStatus$ = new Subject<ErpConnectionStatusEvent>();
  private erpExtractionProgress$ = new Subject<ErpExtractionProgressEvent>();
  private matchingStatus$ = new Subject<MatchingStatusEvent>();

  private ngZone = inject(NgZone);
  private kindeAuth = inject(KindeAuthService);

  /**
   * Get connection status observable
   */
  get isConnected$(): Observable<boolean> {
    return this.connectionStatus$.asObservable();
  }

  /**
   * Get current connection status
   */
  get isConnected(): boolean {
    return this.connectionStatus$.value;
  }

  /**
   * Observable for statement extraction status updates
   */
  get onStatementExtractionStatus$(): Observable<StatementExtractionStatusEvent> {
    return this.statementExtractionStatus$.asObservable();
  }

  /**
   * Observable for AP items status updates
   */
  get onApItemsStatus$(): Observable<ApItemsStatusEvent> {
    return this.apItemsStatus$.asObservable();
  }

  /**
   * Observable for ERP connection status updates
   */
  get onErpConnectionStatus$(): Observable<ErpConnectionStatusEvent> {
    return this.erpConnectionStatus$.asObservable();
  }

  /**
   * Observable for ERP extraction progress updates
   */
  get onErpExtractionProgress$(): Observable<ErpExtractionProgressEvent> {
    return this.erpExtractionProgress$.asObservable();
  }

  /**
   * Observable for matching status updates
   */
  get onMatchingStatus$(): Observable<MatchingStatusEvent> {
    return this.matchingStatus$.asObservable();
  }

  /**
   * Initialize and connect to the socket.io server
   */
  async connect(): Promise<void> {
    if (this.socket) {
      return; // Socket already exists (connected or reconnecting)
    }

    // Use dedicated socket URL from environment
    const socketUrl = environment.socketUrl;

    // Get JWT token from Kinde for authentication
    const token = await this.kindeAuth.getToken();

    this.socket = io(socketUrl, {
      path: "/socket.io", // Configure Socket.IO server path
      transports: ['websocket', 'polling'], // Try websocket first, then fallback to polling
      withCredentials: true, // Required for CORS with credentials
      auth: {
        token // Send JWT token for authentication
      },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // Connection event handlers — run inside NgZone so Angular picks up changes
    this.socket.on('connect', () => {
      console.log('[SocketService] Connected to WebSocket server:', socketUrl);
      console.log('[SocketService] Socket ID:', this.socket?.id);
      this.ngZone.run(() => this.connectionStatus$.next(true));
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[SocketService] Disconnected from WebSocket server. Reason:', reason);
      this.ngZone.run(() => this.connectionStatus$.next(false));
    });

    this.socket.on('connect_error', (error) => {
      console.warn('[SocketService] Connection error:', error.message);
      this.ngZone.run(() => this.connectionStatus$.next(false));
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`[SocketService] Reconnection attempt ${attemptNumber}`);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`[SocketService] Reconnected after ${attemptNumber} attempts`);
    });

    this.socket.on('reconnect_error', () => {
      // Silent — connect_error already logged
    });

    this.socket.on('reconnect_failed', () => {
      console.warn('[SocketService] WebSocket unavailable, real-time updates disabled');
      this.socket?.close();
      this.socket = null;
    });

    // Listen to events from server
    this.socket.on('statement:extraction:status', (data: StatementExtractionStatusEvent) => {
      console.log('[SocketService] Received statement extraction status:', data);
      this.ngZone.run(() => this.statementExtractionStatus$.next(data));
    });

    this.socket.on('statement:ap-items:status', (data: ApItemsStatusEvent) => {
      console.log('[SocketService] Received AP items status:', data);
      this.ngZone.run(() => this.apItemsStatus$.next(data));
    });

    this.socket.on('erp:connection:status', (data: ErpConnectionStatusEvent) => {
      console.log('[SocketService] Received ERP connection status:', data);
      this.ngZone.run(() => this.erpConnectionStatus$.next(data));
    });

    this.socket.on('erp:extraction:progress', (data: ErpExtractionProgressEvent) => {
      console.log('[SocketService] Received ERP extraction progress:', data);
      this.ngZone.run(() => this.erpExtractionProgress$.next(data));
    });

    this.socket.on('matching:status', (data: MatchingStatusEvent) => {
      console.log('[SocketService] Received matching status:', data);
      this.ngZone.run(() => this.matchingStatus$.next(data));
    });
  }

  /**
   * Subscribe to company-specific updates
   */
  async subscribeToCompany(companyId: string): Promise<void> {
    if (!this.socket?.connected) {
      await this.connect();
    }

    this.socket?.emit('subscribe:company', companyId, (response: { success: boolean; room: string }) => {
      console.log('[SocketService] Subscribed to company room:', response.room);
    });
  }

  /**
   * Subscribe to statement-specific updates
   */
  async subscribeToStatement(statementId: string): Promise<void> {
    if (!this.socket?.connected) {
      await this.connect();
    }

    this.socket?.emit('subscribe:statement', statementId, (response: { success: boolean; room: string }) => {
      console.log('[SocketService] Subscribed to statement room:', response.room);
    });
  }

  /**
   * Subscribe to ERP-specific updates
   */
  async subscribeToErp(companyId: string, erpType: string): Promise<void> {
    if (!this.socket?.connected) {
      await this.connect();
    }

    this.socket?.emit('subscribe:erp', { companyId, erpType }, (response: { success: boolean; room: string }) => {
      console.log('[SocketService] Subscribed to ERP room:', response.room);
    });
  }

  /**
   * Unsubscribe from a specific room
   */
  unsubscribe(room: string): void {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit('unsubscribe', room, (response: { success: boolean; room: string }) => {
      console.log('[SocketService] Unsubscribed from room:', response.room);
    });
  }

  /**
   * Disconnect from the socket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connectionStatus$.next(false);
      console.log('[SocketService] Socket disconnected');
    }
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.disconnect();
    this.statementExtractionStatus$.complete();
    this.apItemsStatus$.complete();
    this.erpConnectionStatus$.complete();
    this.erpExtractionProgress$.complete();
    this.matchingStatus$.complete();
    this.connectionStatus$.complete();
  }
}
